import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["status", "details", "enableButton", "disableButton"]
  static values = {
    publicKey: String,
    supported: Boolean,
    createUrl: String,
    destroyUrl: String,
    serviceWorkerUrl: String,
    serviceWorkerScope: String,
    ctaSelector: String
  }

  connect() {
    this.initializeState()
  }

  async enable() {
    if (!this.supportedValue || !("serviceWorker" in navigator) || !("PushManager" in window)) {
      this.renderState("Push alerts are not available on this device.", "You can still read messages in the app.")
      return
    }

    try {
      this.renderState("Turning on push alerts.", "Your browser is setting up device alerts for new messages.")
      const registration = await this.serviceWorkerRegistration()
      const existingSubscription = await registration.pushManager.getSubscription()

      if (existingSubscription) {
        await this.persistSubscription(existingSubscription)
        this.renderEnabled()
        return
      }

      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: this.urlBase64ToUint8Array(this.publicKeyValue)
      })

      await this.persistSubscription(subscription)
      this.renderEnabled()
    } catch (error) {
      this.renderState("Push alerts could not be turned on.", this.errorMessage(error))
    }
  }

  async disable() {
    try {
      const registration = await this.serviceWorkerRegistration()
      const subscription = await registration.pushManager.getSubscription()

      if (subscription) {
        await this.removeSubscription(subscription)
        await subscription.unsubscribe()
      }

      this.renderDisabled()
    } catch (error) {
      this.renderState("Push alerts could not be turned off.", error.message || "Please try again.")
    }
  }

  async persistSubscription(subscription) {
    const json = subscription.toJSON()

    const response = await fetch(this.createUrlValue, {
      method: "POST",
      headers: this.headers(),
      body: JSON.stringify({
        push_subscription: {
          endpoint: json.endpoint,
          p256dh_key: json.keys.p256dh,
          auth_key: json.keys.auth
        }
      })
    })

    if (!response.ok) {
      throw new Error("The app could not save this notification subscription.")
    }
  }

  async removeSubscription(subscription) {
    const response = await fetch(this.destroyUrlValue, {
      method: "DELETE",
      headers: this.headers(),
      body: JSON.stringify({ endpoint: subscription.endpoint })
    })

    if (!response.ok && response.status !== 204) {
      throw new Error("The app could not remove this notification subscription.")
    }
  }

  headers() {
    return {
      "Content-Type": "application/json",
      "Accept": "application/json",
      "X-CSRF-Token": document.querySelector("meta[name='csrf-token']").content
    }
  }

  renderEnabled() {
    this.renderState(
      "Push alerts are on for this browser.",
      "This browser can show an alert when your care team sends you a new message."
    )
    this.enableButtonTarget.hidden = true
    this.disableButtonTarget.hidden = false
    this.toggleCta(false)
  }

  renderDisabled() {
    this.renderState(
      "Push alerts are off.",
      "You can still read messages in the app. This browser just will not show device alerts."
    )
    this.enableButtonTarget.hidden = false
    this.disableButtonTarget.hidden = true
    this.toggleCta(true)
  }

  renderState(title, body) {
    this.statusTarget.textContent = title
    this.detailsTarget.textContent = body
  }

  syncButtons() {
    if (!this.supportedValue) {
      this.enableButtonTarget.disabled = true
      return
    }

    if (this.disableButtonTarget.hidden) {
      this.enableButtonTarget.hidden = false
    } else {
      this.enableButtonTarget.hidden = true
    }
  }

  async initializeState() {
    if (!this.supportedValue || !("serviceWorker" in navigator) || !("PushManager" in window)) {
      this.renderState("Push alerts are not available on this device.", "You can still read messages in the app.")
      this.enableButtonTarget.disabled = true
      this.disableButtonTarget.hidden = true
      this.toggleCta(false)
      return
    }

    try {
      const registration = await this.serviceWorkerRegistration()
      const existingSubscription = await registration.pushManager.getSubscription()

      if (existingSubscription) {
        this.renderEnabled()
      } else {
        this.renderDisabled()
      }
    } catch (error) {
      this.renderState("Push alerts could not be checked for this browser.", this.errorMessage(error))
    }

    this.syncButtons()
  }

  urlBase64ToUint8Array(base64String) {
    const padding = "=".repeat((4 - base64String.length % 4) % 4)
    const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/")
    const rawData = window.atob(base64)

    return Uint8Array.from([...rawData].map((char) => char.charCodeAt(0)))
  }

  async serviceWorkerRegistration() {
    if (this.hasServiceWorkerUrlValue) {
      await navigator.serviceWorker.register(this.serviceWorkerUrlValue, {
        scope: this.hasServiceWorkerScopeValue ? this.serviceWorkerScopeValue : "/"
      })
    }

    return this.serviceWorkerReady()
  }

  serviceWorkerReady() {
    return Promise.race([
      navigator.serviceWorker.ready,
      new Promise((_, reject) => {
        window.setTimeout(() => reject(new Error("The browser push alert setup did not become ready.")), 10000)
      })
    ])
  }

  errorMessage(error) {
    const message = error?.message || ""

    if (message.toLowerCase().includes("push service error")) {
      return "Your messages are still on. This browser cannot set up pop-up alerts right now. Try Chrome or Safari, or check that browser notifications are allowed."
    }

    if (message.toLowerCase().includes("permission")) {
      return "Your messages are still on. Browser notification permission was not granted."
    }

    return message || "Please try again."
  }

  toggleCta(visible) {
    if (!this.hasCtaSelectorValue) return

    const cta = document.querySelector(this.ctaSelectorValue)
    if (!cta) return

    cta.hidden = !visible
  }
}
